import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const ReceiveLocationStep = ({
  formData,
  handleChange,
  warehouses = [],
  racks = [],
  bays = [],
  selectedJobForAssignment = null
}) => {
  // Filter racks and bays based on selected warehouse (if using hierarchical structure)
  const racksArray = Array.isArray(racks) ? racks : [];
  const baysArray = Array.isArray(bays) ? bays : [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.primary' }}>
        Step 3: Location Assignment {selectedJobForAssignment ? '(Job Stack Location)' : '(Optional)'}
      </Typography>
      
      {/* Show job assignment info if equipment is assigned to a job */}
      {selectedJobForAssignment && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: '#e8f5e9',
          borderRadius: 1,
          border: '1px solid #c8e6c9'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
            ✓ Equipment will be assigned to job
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            <strong>Job:</strong> {selectedJobForAssignment.jobId || selectedJobForAssignment.id} - {selectedJobForAssignment.jobName || selectedJobForAssignment.name || 'Unknown'}
          </Typography>
          {selectedJobForAssignment.jobClient && (
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              <strong>Client:</strong> {selectedJobForAssignment.jobClient}
            </Typography>
          )}
          {(() => {
            // Get first stack location from new multi-location format, or fall back to old single fields
            const firstLocation = selectedJobForAssignment.stackLocations?.[0];
            const hasStackLocation = firstLocation || selectedJobForAssignment.stackWarehouseId || selectedJobForAssignment.stackRackId || selectedJobForAssignment.stackBayId;

            if (!hasStackLocation) return null;

            // Warehouse can be in stackLocations array (new format) OR at job level (old format)
            // Try new format first, then fall back to old format
            let warehouseDisplay = firstLocation?.warehouseName; // New format: in stackLocations array

            if (!warehouseDisplay && firstLocation?.warehouseId) {
              // Have warehouse ID from new format - look up the name
              const warehouse = warehouses.find(w => w.id === firstLocation.warehouseId);
              warehouseDisplay = warehouse?.name || firstLocation.warehouseId;
            }

            // Fall back to old single-location format
            if (!warehouseDisplay) {
              warehouseDisplay = selectedJobForAssignment.stackWarehouseName;
            }

            if (!warehouseDisplay && selectedJobForAssignment.stackWarehouseId) {
              // Look up warehouse name from ID in old format
              const warehouse = warehouses.find(w => w.id === selectedJobForAssignment.stackWarehouseId);
              warehouseDisplay = warehouse?.name || selectedJobForAssignment.stackWarehouseId;
            }

            if (!warehouseDisplay) {
              warehouseDisplay = 'Not specified';
            }

            // Rack and bay ARE in stackLocations array (new format) or job level (old format)
            const rackDisplay = firstLocation?.rackName || selectedJobForAssignment.stackRackName || selectedJobForAssignment.stackRackId || 'Not specified';
            const bayDisplay = firstLocation?.bayName || selectedJobForAssignment.stackBayName || selectedJobForAssignment.stackBayId || 'Not specified';

            return (
              <Box sx={{ mt: 1, p: 1, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                  PLACE EQUIPMENT AT JOB STACK LOCATION:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>Warehouse:</strong> {warehouseDisplay} •
                  <strong> Rack:</strong> {rackDisplay} •
                  <strong> Bay:</strong> {bayDisplay}
                </Typography>
              </Box>
            );
          })()}
        </Box>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: '800px' }}>
          {/* Warehouse Selection */}
          <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }} size="small">
            <InputLabel id="warehouse-label">Warehouse</InputLabel>
            <Select
              labelId="warehouse-label"
              name="warehouse"
              value={formData.warehouse || ''}
              onChange={handleChange}
              label="Warehouse"
            >
              <MenuItem value="">
                <em>None (Optional)</em>
              </MenuItem>
              {warehouses.map(warehouse => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Rack Selection */}
          <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }} size="small">
            <InputLabel id="rack-label">Rack</InputLabel>
            <Select
              labelId="rack-label"
              id="rack-select"
              name="rack"
              value={formData.rack || ''}
              onChange={handleChange}
              label="Rack"
            >
              <MenuItem value="">
                <em>None (Optional)</em>
              </MenuItem>
              {racksArray
                .sort((a, b) => {
                  const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                  const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                  return aNum - bNum;
                })
                .map(rack => (
                  <MenuItem key={rack.id} value={rack.id}>
                    {rack.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Bay Selection */}
          <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }} size="small">
            <InputLabel id="bay-label">Bay</InputLabel>
            <Select
              labelId="bay-label"
              id="bay-select"
              name="bay"
              value={formData.bay || ''}
              onChange={handleChange}
              label="Bay"
            >
              <MenuItem value="">
                <em>None (Optional)</em>
              </MenuItem>
              {baysArray
                .sort((a, b) => {
                  const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                  const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                  return aNum - bNum;
                })
                .map(bay => (
                  <MenuItem key={bay.id} value={bay.id}>
                    {bay.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          💡 Location assignment is optional. You can assign equipment to specific warehouse locations or leave blank for general inventory.
        </Typography>
      </Box>
    </Box>
  );
};

export default ReceiveLocationStep;